import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { SOLUCOES_LABELS } from '@/pages/painel/lib/radarConstants';
import {
  type OportunidadeConsolidada,
  FAIXA_LABELS,
  FAIXA_TONES,
  FAIXA_ICONS,
  MARGEM_TONES,
  CUSTO_KEYS,
  ORIGEM_LABELS,
  formatBRL,
  formatPercent,
  formatDate,
} from '@/pages/painel/lib/comercial';
import { calcularFinanceiro } from '@/pages/painel/lib/financeiro';

interface Props {
  oportunidade: OportunidadeConsolidada;
  password: string;
  onClose: () => void;
  onSaved: () => void;
}

const ORIGEM_LABEL: Record<string, string> = { crm: 'CRM', radar: 'Radar' };

const ETAPAS_CRM = ['novo', 'pesquisar_contato', 'contato_iniciado', 'em_conversa', 'reuniao', 'proposta', 'negociacao', 'ganho', 'perdido', 'acompanhar'];

interface HistoricoRow {
  id: number;
  evento: string;
  detalhe: any;
  created_at: string;
}

function showBRL(v: number | null | undefined, empty = 'Não calculável'): string {
  return v == null ? empty : formatBRL(v);
}

function showPct(v: number | null | undefined, empty = 'Não calculável'): string {
  return v == null ? empty : formatPercent(v);
}

export default function OportunidadeDetalheModal({ oportunidade, password, onClose, onSaved }: Props) {
  const [historico, setHistorico] = useState<HistoricoRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [fornecedoresConsultados, setFornecedoresConsultados] = useState<any[]>([]);
  const [fornecedoresDisponiveis, setFornecedoresDisponiveis] = useState<any[]>([]);
  const [fornecedorSel, setFornecedorSel] = useState('');
  const [fornecedorProduto, setFornecedorProduto] = useState('');
  const [fornecedorPreco, setFornecedorPreco] = useState('');
  const [fornSaving, setFornSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Formulário oficial
  const [valor, setValor] = useState<string>(oportunidade.valor != null ? String(oportunidade.valor) : '');
  const [probabilidade, setProbabilidade] = useState<string>(oportunidade.probabilidade != null ? String(oportunidade.probabilidade) : '');
  const [etapa, setEtapa] = useState<string>(oportunidade.etapa ?? '');
  const [responsavel, setResponsavel] = useState(oportunidade.responsavel ?? '');
  const [proximaAcao, setProximaAcao] = useState(oportunidade.proxima_acao ?? '');
  const [proximaAcaoEm, setProximaAcaoEm] = useState(oportunidade.proxima_acao_em ?? '');
  const [previsaoFechamento, setPrevisaoFechamento] = useState(oportunidade.previsao_fechamento ?? '');
  const [solucoes, setSolucoes] = useState<string[]>(oportunidade.solucoes ?? []);
  const [custos, setCustos] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const c of CUSTO_KEYS) init[c.key] = oportunidade.custos?.[c.key] != null ? String(oportunidade.custos[c.key]) : '';
    return init;
  });

  // Simulador (what-if temporário — não altera os dados oficiais até "Aplicar")
  const [simValor, setSimValor] = useState<string>(oportunidade.valor != null ? String(oportunidade.valor) : '');
  const [simCusto, setSimCusto] = useState<string>(() => {
    const total = CUSTO_KEYS.reduce((s, c) => s + Number(oportunidade.custos?.[c.key] ?? 0), 0);
    return total > 0 ? String(total) : '';
  });
  const [simProb, setSimProb] = useState<string>(oportunidade.probabilidade != null ? String(oportunidade.probabilidade) : '');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'detalhar', password, origem: oportunidade.origem, id: oportunidade.crm_id ?? oportunidade.radar_id },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { historico?: HistoricoRow[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setHistorico(parsed?.historico ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar detalhe.');
    } finally {
      setLoading(false);
    }
  };

  const loadFornecedores = async () => {
    const opId = oportunidade.crm_id;
    if (!opId) return;
    const [consRes, dispRes] = await Promise.all([
      supabase.functions.invoke('filas-admin', { body: { action: 'oportunidade_fornecedores_listar', password, oportunidade_id: opId } }),
      supabase.functions.invoke('filas-admin', { body: { action: 'fornecedor_listar', password } }),
    ]);
    if (!consRes.error && !consRes.data?.error) setFornecedoresConsultados(consRes.data?.itens ?? []);
    if (!dispRes.error && !dispRes.data?.error) setFornecedoresDisponiveis(dispRes.data?.fornecedores ?? []);
  };

  const addFornecedor = async () => {
    if (!fornecedorSel || !oportunidade.crm_id) return;
    setFornSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('filas-admin', {
        body: {
          action: 'oportunidade_fornecedor_salvar',
          password,
          oportunidade_id: oportunidade.crm_id,
          fornecedor_id: Number(fornecedorSel),
          produto: fornecedorProduto.trim() || null,
          preco: fornecedorPreco !== '' ? Number(fornecedorPreco) : null,
        },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      setFornecedorSel('');
      setFornecedorProduto('');
      setFornecedorPreco('');
      await loadFornecedores();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao vincular fornecedor.');
    } finally {
      setFornSaving(false);
    }
  };

  const removeFornecedor = async (id: number) => {
    await supabase.functions.invoke('filas-admin', { body: { action: 'oportunidade_fornecedor_excluir', password, id } });
    await loadFornecedores();
  };

  useEffect(() => {
    load();
    loadFornecedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oportunidade.id]);

  // Cálculo financeiro centralizado (fonte única).
  const fin = useMemo(() => calcularFinanceiro(valor, custos, probabilidade), [valor, custos, probabilidade]);
  const simFin = useMemo(() => {
    const custoObj: Record<string, string> = {};
    if (simCusto) custoObj.produto = simCusto;
    return calcularFinanceiro(simValor, custoObj, simProb);
  }, [simValor, simCusto, simProb]);

  const margemTone = fin.margemTone ? MARGEM_TONES[fin.margemTone] : null;
  const simTone = simFin.margemTone ? MARGEM_TONES[simFin.margemTone] : null;

  const aplicarSimulador = () => {
    setValor(simValor);
    setProbabilidade(simProb);
    setCustos((prev) => {
      const next: Record<string, string> = {};
      for (const c of CUSTO_KEYS) next[c.key] = c.key === 'produto' ? simCusto : '';
      return next;
    });
  };

  const salvar = async () => {
    setSaving(true);
    setError(null);
    setMsg(null);
    try {
      const custosObj: Record<string, number> = {};
      for (const c of CUSTO_KEYS) {
        const v = Number(custos[c.key] ?? 0);
        if (v > 0) custosObj[c.key] = v;
      }
      const valorNum = Number(valor);
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: {
          action: 'salvar',
          password,
          origem: oportunidade.origem,
          id: oportunidade.crm_id ?? oportunidade.radar_id,
          valor: valor.trim() !== '' && !Number.isNaN(valorNum) ? valorNum : null,
          custos: Object.keys(custosObj).length ? custosObj : null,
          probabilidade: probabilidade ? Number(probabilidade) : null,
          etapa,
          solucoes,
          responsavel,
          proxima_acao: proximaAcao,
          proxima_acao_em: proximaAcaoEm || null,
          previsao_fechamento: previsaoFechamento || null,
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setMsg('Alterações salvas.');
      onSaved();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const promover = async () => {
    setPromoting(true);
    setError(null);
    setMsg(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'promover_projeto', password, origem: oportunidade.origem, id: oportunidade.crm_id ?? oportunidade.radar_id },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { ok?: boolean; existente?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setMsg(parsed?.existente ? 'Este projeto estratégico já existe.' : 'Oportunidade promovida a Projeto Estratégico.');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao promover.');
    } finally {
      setPromoting(false);
    }
  };

  const toggleSolucao = (key: string) => {
    setSolucoes((prev) => (prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl my-6 border border-gray-200">
        {/* Cabeçalho */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4 sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${FAIXA_TONES[oportunidade.faixa]}`}>
                <i className={FAIXA_ICONS[oportunidade.faixa]}></i>
                {FAIXA_LABELS[oportunidade.faixa]}
              </span>
              <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                {ORIGEM_LABEL[oportunidade.origem]}
              </span>
              {oportunidade.ganha && (
                <span className="text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Ganha</span>
              )}
              {oportunidade.perdida && (
                <span className="text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Perdida</span>
              )}
            </div>
            <h3 className="text-lg font-bold text-gray-900 leading-snug">{oportunidade.titulo}</h3>
            {oportunidade.empresa && <p className="text-sm text-gray-500">{oportunidade.empresa}</p>}
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 cursor-pointer shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
          {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{msg}</div>}

          {/* Atalhos */}
          <div className="flex items-center gap-2 flex-wrap">
            {oportunidade.empresa_id && (
              <Link to={`/painel/clientes/${oportunidade.empresa_id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap">
                <i className="ri-building-4-line"></i> Empresa
              </Link>
            )}
            {oportunidade.radar_id && (
              <Link to={`/painel/radar/oportunidades/${oportunidade.radar_id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap">
                <i className="ri-radar-line"></i> Radar
              </Link>
            )}
            {oportunidade.projeto_estrategico_id ? (
              <Link to="/painel/projetos-estrategicos" className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap">
                <i className="ri-focus-3-line"></i> Projeto Estratégico
              </Link>
            ) : (
              <button onClick={promover} disabled={promoting} className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap disabled:opacity-60">
                {promoting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-focus-3-line"></i>}
                Promover para Projeto Estratégico
              </button>
            )}
          </div>

          {/* Resumo */}
          <section>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Resumo</h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[11px] text-gray-500">Cidade / UF</p>
                <p className="text-sm font-semibold text-gray-900">{[oportunidade.cidade, oportunidade.uf].filter(Boolean).join(' - ') || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[11px] text-gray-500">Origem</p>
                <p className="text-sm font-semibold text-gray-900">{ORIGEM_LABELS[oportunidade.fonte ?? ''] ?? oportunidade.fonte ?? '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[11px] text-gray-500">Estágio / Etapa</p>
                <p className="text-sm font-semibold text-gray-900">{oportunidade.etapa ?? '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[11px] text-gray-500">Criado em</p>
                <p className="text-sm font-semibold text-gray-900">{formatDate(oportunidade.created_at)}</p>
              </div>
            </div>
          </section>

          {/* Financeiro */}
          <section>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Financeiro</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-gray-500 font-semibold">Valor estimado da venda (R$)</label>
                    <input type="number" value={valor} onChange={(e) => setValor(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 font-semibold">Probabilidade (%)</label>
                    <input type="number" min="0" max="100" value={probabilidade} onChange={(e) => setProbabilidade(e.target.value)} placeholder="0–100" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[11px] text-gray-500 font-semibold">Custos estimados (opcional — não informado = em branco)</p>
                  {CUSTO_KEYS.map((c) => (
                    <div key={c.key} className="flex items-center gap-2">
                      <span className="w-28 text-xs text-gray-600 whitespace-nowrap">{c.label}</span>
                      <input
                        type="number"
                        value={custos[c.key]}
                        onChange={(e) => setCustos((prev) => ({ ...prev, [c.key]: e.target.value }))}
                        placeholder="Não informado"
                        className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Resumo financeiro (todos os indicadores, fonte única) */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 h-fit">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Valor da venda</span>
                  <span className="text-sm font-bold text-gray-900">{fin.valor != null ? formatBRL(fin.valor) : 'Não informado'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Custo total</span>
                  <span className="text-sm font-semibold text-gray-900">{fin.temCustos ? formatBRL(fin.custoTotal) : 'Não informado'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Lucro estimado</span>
                  <span className={`text-sm font-bold ${fin.lucro != null ? (fin.lucro >= 0 ? 'text-green-700' : 'text-red-600') : 'text-gray-400'}`}>
                    {showBRL(fin.lucro)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Margem estimada</span>
                  <span className={`text-sm font-bold ${margemTone ? 'text-gray-900' : 'text-gray-400'}`}>{showPct(fin.margem)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Probabilidade</span>
                  <span className="text-sm font-semibold text-gray-900">{fin.probabilidade != null ? formatPercent(fin.probabilidade) : 'Não informado'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Pipeline ponderado</span>
                  <span className="text-sm font-semibold text-gray-900">{showBRL(fin.pipelinePonderado)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Lucro ponderado</span>
                  <span className="text-sm font-semibold text-gray-900">{showBRL(fin.lucroPonderado)}</span>
                </div>
                {margemTone && (
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${margemTone.tone}`}>
                    <i className={margemTone.icon}></i> {margemTone.label}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Simulador */}
          <section className="bg-amber-50/60 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <i className="ri-calculator-line"></i>
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900">Simulador</h4>
                <p className="text-[11px] text-gray-500">Teste cenários sem alterar os dados oficiais — só muda ao clicar em "Aplicar valores".</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Valor de venda (R$)</label>
                <input type="number" value={simValor} onChange={(e) => setSimValor(e.target.value)} className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Custo total (R$)</label>
                <input type="number" value={simCusto} onChange={(e) => setSimCusto(e.target.value)} className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Probabilidade (%)</label>
                <input type="number" min="0" max="100" value={simProb} onChange={(e) => setSimProb(e.target.value)} className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#E8610A]" />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-white rounded-lg px-3 py-2 border border-amber-100">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Lucro</p>
                <p className={`text-sm font-bold ${simFin.lucro != null ? (simFin.lucro >= 0 ? 'text-green-700' : 'text-red-600') : 'text-gray-400'}`}>{showBRL(simFin.lucro)}</p>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-amber-100">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Margem</p>
                <p className={`text-sm font-bold ${simTone ? 'text-gray-900' : 'text-gray-400'}`}>{showPct(simFin.margem)}</p>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-amber-100">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Pipeline ponderado</p>
                <p className="text-sm font-bold text-gray-900">{showBRL(simFin.pipelinePonderado)}</p>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-amber-100">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Lucro ponderado</p>
                <p className="text-sm font-bold text-gray-900">{showBRL(simFin.lucroPonderado)}</p>
              </div>
            </div>

            {simTone && (
              <div className="mt-2">
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${simTone.tone}`}>
                  <i className={simTone.icon}></i> {simTone.label}
                </span>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-gray-500">Ao aplicar, o custo total vira a linha "Produto" e as demais linhas são zeradas.</p>
              <button onClick={aplicarSimulador} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap">
                <i className="ri-play-list-add-line"></i> Aplicar valores à oportunidade
              </button>
            </div>
          </section>

          {/* Soluções */}
          <section>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Soluções</h4>
            <div className="flex items-center gap-1.5 flex-wrap">
              {Object.entries(SOLUCOES_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => toggleSolucao(key)}
                  className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer whitespace-nowrap ${
                    solucoes.includes(key) ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]'
                  }`}
                >
                  {solucoes.includes(key) && <i className="ri-check-line"></i>}
                  {label}
                </button>
              ))}
            </div>
          </section>

          {/* Fornecedores consultados */}
          {oportunidade.crm_id && (
            <section>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Fornecedores consultados</h4>
              {fornecedoresConsultados.length === 0 ? (
                <p className="text-xs text-gray-400 mb-3">Nenhum fornecedor vinculado a esta oportunidade.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {fornecedoresConsultados.map((it) => (
                    <div key={it.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{it.fornecedores?.empresas?.nome ?? 'Fornecedor'}</p>
                        <p className="text-xs text-gray-500">
                          {it.produto}{it.preco != null ? ` · ${formatBRL(it.preco)}` : ''}{it.condicao ? ` · ${it.condicao}` : ''}
                        </p>
                      </div>
                      <button onClick={() => removeFornecedor(it.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer shrink-0">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <select value={fornecedorSel} onChange={(e) => setFornecedorSel(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
                  <option value="">Selecionar fornecedor...</option>
                  {fornecedoresDisponiveis.map((f) => (
                    <option key={f.id} value={f.id}>{f.empresas?.nome ?? 'Fornecedor'}</option>
                  ))}
                </select>
                <input value={fornecedorProduto} onChange={(e) => setFornecedorProduto(e.target.value)} placeholder="Produto" className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                <input type="number" value={fornecedorPreco} onChange={(e) => setFornecedorPreco(e.target.value)} placeholder="Cotação R$" className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                <button onClick={addFornecedor} disabled={fornSaving || !fornecedorSel} className="inline-flex items-center justify-center gap-1 text-sm font-semibold text-white bg-gray-800 hover:bg-gray-700 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap disabled:opacity-50">
                  {fornSaving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Vincular
                </button>
              </div>
            </section>
          )}

          {/* Próximas ações */}
          <section>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Próximas ações</h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Responsável</label>
                <input type="text" value={responsavel} onChange={(e) => setResponsavel(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Próxima ação</label>
                <input type="text" value={proximaAcao} onChange={(e) => setProximaAcao(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Data da próxima ação</label>
                <input type="date" value={proximaAcaoEm} onChange={(e) => setProximaAcaoEm(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Previsão de fechamento</label>
                <input type="date" value={previsaoFechamento} onChange={(e) => setPrevisaoFechamento(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-semibold">Etapa (CRM)</label>
                <select value={etapa} onChange={(e) => setEtapa(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
                  <option value="">—</option>
                  {ETAPAS_CRM.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Origem dos dados */}
          <section>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Origem dos dados</h4>
            <div className="bg-gray-50 rounded-xl p-4 space-y-1.5">
              {[
                ['Valor da venda', 'manual'],
                ['Custo total', 'manual'],
                ['Probabilidade', 'manual'],
                ['Lucro estimado', 'calculado'],
                ['Margem estimada', 'calculada'],
                ['Pipeline ponderado', 'calculado'],
                ['Lucro ponderado', 'calculado'],
              ].map(([campo, origem]) => (
                <div key={campo} className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-gray-600">{campo}</span>
                  <span className={`inline-flex items-center gap-1 font-semibold rounded-full px-2 py-0.5 ${origem === 'manual' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                    <i className={origem === 'manual' ? 'ri-edit-line' : 'ri-calculator-line'}></i> {origem}
                  </span>
                </div>
              ))}
            </div>

            {oportunidade.origem_dados && (
              <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-4 mt-3">
                <p className="text-[11px] font-semibold text-amber-800 mb-1.5">Procedência da obra/CAU</p>
                {typeof oportunidade.origem_dados === 'object' && oportunidade.origem_dados.campos ? (
                  <div className="space-y-1.5">
                    {Object.entries(oportunidade.origem_dados.campos).map(([k, v]) => (
                      <div key={k} className="flex items-start justify-between gap-3 text-xs">
                        <span className="text-gray-600">{k}</span>
                        <span className="text-gray-400 text-right">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">{JSON.stringify(oportunidade.origem_dados)}</p>
                )}
              </div>
            )}
          </section>

          {/* Histórico */}
          <section>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Histórico</h4>
            {loading ? (
              <p className="text-xs text-gray-400">Carregando...</p>
            ) : historico.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhuma alteração registrada ainda.</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {historico.map((h) => (
                  <div key={h.id} className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-history-line text-gray-400"></i>
                    <span className="font-semibold">{h.evento}</span>
                    <span className="text-gray-400">{formatDate(h.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Rodapé */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap">
            Fechar
          </button>
          <button onClick={salvar} disabled={saving} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-5 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60">
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  );
}